import React from 'react';
import { Typography } from 'antd';

const { Title, Text } = Typography;

export interface PageShellProps {
  title: string;
  description?: string;
  /** Primary action(s) on the right; title and description stay grouped on the left (stacks on small screens). */
  titleEnd?: React.ReactNode;
  /** Optional row under the description (e.g. date chips, status badges). */
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

/**
 * Consistent page title + intro for vendor pages (readable on phones, calm for non-technical users).
 */
const PageShell: React.FC<PageShellProps> = ({ title, description, titleEnd, headerExtra, children, className }) => {
  return (
    <div className={['vendor-page w-full animate-fade-in', className].filter(Boolean).join(' ')}>
      <header className={titleEnd ? 'mb-5 sm:mb-7' : 'mb-6 sm:mb-8'}>
        {titleEnd ? (
          <div className="flex flex-col gap-3 min-[480px]:flex-row min-[480px]:items-center min-[480px]:justify-between min-[480px]:gap-4 lg:gap-6">
            <div className="min-w-0 flex-1 pr-0 min-[480px]:pr-4">
              <Title
                level={2}
                className="!mb-0 !text-xl !font-bold !leading-snug !text-gray-900 sm:!text-2xl"
              >
                {title}
              </Title>
              {description ? (
                <Text className="mt-1 block max-w-3xl text-sm leading-snug text-gray-600 sm:mt-1.5">
                  {description}
                </Text>
              ) : null}
            </div>
            <div className="vendor-page-title-actions flex w-full min-w-0 shrink-0 flex-wrap items-stretch justify-stretch gap-2 min-[480px]:w-auto min-[480px]:items-center min-[480px]:justify-end">
              {titleEnd}
            </div>
          </div>
        ) : (
          <>
            <Title
              level={2}
              className="!mb-1.5 !text-2xl !font-bold !leading-tight !text-gray-900 sm:!mb-2 sm:!text-3xl"
            >
              {title}
            </Title>
            {description ? (
              <Text className="mt-0 block max-w-3xl text-sm leading-snug text-gray-600">
                {description}
              </Text>
            ) : null}
          </>
        )}
        {headerExtra ? <div className="mt-2 flex flex-wrap gap-2 sm:mt-3">{headerExtra}</div> : null}
      </header>
      {children}
    </div>
  );
};

export default PageShell;
